import React, { useEffect, useState } from 'react';
import RegGate from '../components/RegGate';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Building2, MapPin, Calendar, Clock, Mail, Globe, Ban } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { CORPORATE_INDUSTRIES, CORPORATE_BOOTH_NUMBERS, CORPORATE_BOOTH_INFO, CORPORATE_BOOTH_LAYOUT, FLOORPLAN_URL, LOGO_URL } from '../data/links';
import Marquee from '../components/Marquee';

function qp() { try { return new URLSearchParams(window.location.search); } catch (e) { return null; } }
function getUtm() {
  const p = qp();
  const o = { utm_source: '', utm_medium: '', utm_campaign: '' };
  if (p) ['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => { if (p.get(k)) o[k] = p.get(k); });
  if (!o.utm_source) { try { const s = JSON.parse(localStorage.getItem('tv_utm') || '{}'); ['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => { if (!o[k] && s[k]) o[k] = s[k]; }); } catch (e) {} }
  return o;
}
function tvTrack(event) {
  try {
    const u = getUtm();
    fetch('/api/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true, body: JSON.stringify({ link_id: 'corporate', label: 'BRAND BOOTH REGISTRATION', url: '/corporate', event, referrer: document.referrer || '', user_agent: navigator.userAgent, ...u }) }).catch(() => {});
  } catch (e) {}
}

function CorporatePageForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: '',
    industry: '',
    other_industry: '',
    company_website: '',
    pic_name: '',
    pic_position: '',
    pic_email: '',
    pic_phone: '',
    booth_number: '',
  });
  const [showOtherIndustry, setShowOtherIndustry] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [taken, setTaken] = useState({ approved: [], pending: [] });

  useEffect(() => {
    const p = qp();
    if (p) { const g = {}; let any = false; ['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => { if (p.get(k)) { g[k] = p.get(k); any = true; } }); if (any) { try { localStorage.setItem('tv_utm', JSON.stringify(g)); } catch (e) {} } }
    tvTrack('page_view');
    const sid = (() => { try { let s = sessionStorage.getItem('tv_sid'); if (!s) { s = Date.now().toString(36) + Math.random().toString(36).slice(2); sessionStorage.setItem('tv_sid', s); } return s; } catch (e) { return 'anon'; } })();
    const png = () => { try { fetch('/api/presence/ping', { method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true, body: JSON.stringify({ session_id: sid, path: window.location.pathname }) }).catch(() => {}); } catch (e) {} };
    png();
    const pint = setInterval(png, 20000);
    api.get('/corporate/booths/taken').then(({ data }) => {
      setTaken({ approved: data.approved || [], pending: data.pending || [] });
    }).catch(() => {});
    return () => clearInterval(pint);
  }, []);

  const [started, setStarted] = useState(false);
  const onFirstInteract = () => { if (!started) { setStarted(true); tvTrack('form_start'); } };

  const isApproved = (n) => taken.approved.includes(n);
  const isPending = (n) => taken.pending.includes(n);
  const isSelectable = (n) => !isApproved(n); // pending is still selectable (visual warning only)

  // F&B / Food & Beverage detection (from dropdown OR from Other input)
  const isFnB = (() => {
    const s = ((form.industry || '') + ' ' + (form.other_industry || '')).toLowerCase();
    return /\bf\s*&\s*b\b|\bfnb\b|food\s*&?\s*(and)?\s*beverage|makanan|minuman|kuliner/.test(s);
  })();

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const selectIndustry = (val) => { setShowOtherIndustry(false); setForm((s) => ({ ...s, industry: val, other_industry: '' })); };
  const selectBooth = (n) => {
    if (!isSelectable(n)) return;
    update('booth_number', n);
    // Auto-scroll booth field into view briefly
    const el = document.getElementById('booth-picker');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isFnB) {
      toast({ title: 'Pendaftaran F&B belum dibuka', description: 'Pendaftaran F&B / Food & Beverage akan dibuka pada Oktober–November 2026.', variant: 'destructive' });
      return;
    }
    const required = ['company_name', 'pic_name', 'pic_email', 'pic_phone'];
    for (const k of required) {
      if (!String(form[k] || '').trim()) {
        toast({ title: 'Field wajib belum diisi', description: 'Mohon lengkapi isian bertanda *', variant: 'destructive' });
        return;
      }
    }
    if (!form.industry && !form.other_industry.trim()) {
      toast({ title: 'Pilih industry', description: 'Pilih salah satu industry atau isi Other.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/corporate/register', { ...form, booth_package: 'Premium Booth', ...getUtm(), referrer: document.referrer || '' });
      setSuccess(true);
      toast({ title: 'Pendaftaran terkirim!', description: 'Panitia akan menghubungi Anda dalam 2×24 jam.' });
    } catch (err) {
      toast({ title: 'Gagal mengirim', description: err?.response?.data?.detail || 'Silakan coba lagi.', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (success) return <SuccessScreen onNew={() => { setSuccess(false); setForm({ company_name:'', industry:'', other_industry:'', company_website:'', pic_name:'', pic_position:'', pic_email:'', pic_phone:'', booth_number:'' }); setShowOtherIndustry(false); }} onHome={() => navigate('/')} />;

  return (
    <div className="min-h-screen w-full bg-[#E63329]">
      <Marquee />
      <Toaster />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-white/95 hover:text-white text-xs font-bold tracking-widest uppercase mb-4">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </Link>

        <div className="flex flex-col items-center text-center mb-6">
          <img src={LOGO_URL} alt="TalesVerse" className="w-[220px] md:w-[260px] h-auto drop-shadow-[4px_4px_0_rgba(0,0,0,0.25)]" />
          <div className="mt-3">
            <span className="tv-chip" style={{ backgroundColor: '#F5C518' }}>BRAND BOOTH REGISTRATION</span>
          </div>
          <h1 className="outline-title text-3xl md:text-4xl mt-4 leading-none">Brand Registration</h1>
          <p className="text-white/95 text-[13px] font-medium mt-2 max-w-md">
            Formulir untuk <b>brand</b> yang ingin membuka booth premium di TalesVerse 2026. Tim kami akan menghubungi kamu dalam 2×24 jam.
          </p>
        </div>

        {/* Booth details */}
        <div className="bg-white text-[#1a1a1a] border-[2.5px] border-[#1a1a1a] rounded-2xl p-5 shadow-[6px_6px_0_#1a1a1a] mb-5">
          <div className="grid md:grid-cols-3 gap-3 text-[13px] mb-4">
            <InfoRow icon={<Calendar className="w-4 h-4 text-[#E63329]" />} label="Sat–Sun, 12–13 Dec 2026" />
            <InfoRow icon={<Clock className="w-4 h-4 text-[#E63329]" />} label="10.00 – 21.00 WIB" />
            <InfoRow icon={<MapPin className="w-4 h-4 text-[#E63329]" />} label="The Brickhall Fatmawati, Jakarta" />
          </div>

          <div className="border-t-2 border-dashed border-black/15 pt-4">
            <div className="flex flex-wrap items-baseline gap-2 mb-2">
              <span className="tv-chip" style={{ backgroundColor: '#F5C518', boxShadow: 'none', padding: '4px 10px', fontSize: 11 }}>EARLY PACKAGE</span>
              <span className="font-display text-[20px] font-bold text-[#E63329]">{CORPORATE_BOOTH_INFO.price}</span>
              <span className="text-[13px] text-black/50 line-through decoration-2">{CORPORATE_BOOTH_INFO.price_original}</span>
              <span className="text-[12px] text-black/60">({CORPORATE_BOOTH_INFO.price_note})</span>
            </div>
            <p className="text-[12px] text-black/70 mb-3">Area <b>{CORPORATE_BOOTH_INFO.size}</b> — indoor.</p>
            <ul className="space-y-1.5">
              {CORPORATE_BOOTH_INFO.includes.map((item, i) => (
                <li key={i} className="text-[13px] flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} onFocusCapture={onFirstInteract} className="bg-white text-[#1a1a1a] border-[2.5px] border-[#1a1a1a] rounded-2xl p-5 md:p-7 shadow-[6px_6px_0_#1a1a1a] space-y-5">
          <Field label="COMPANY NAME" required>
            <Input value={form.company_name} onChange={(v) => update('company_name', v)} placeholder="PT / CV / Brand name" leftIcon={<Building2 className="w-4 h-4 text-black/50"/>} />
          </Field>

          <Field label="INDUSTRY" required hint="Pilih satu">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CORPORATE_INDUSTRIES.filter((i) => i !== 'Other').map((c) => {
                const active = form.industry === c;
                return (
                  <button key={c} type="button" onClick={() => selectIndustry(c)}
                    className={`text-left text-[12px] font-bold py-2.5 px-3 rounded-lg border-2 transition-colors flex items-center gap-2 ${active ? 'bg-[#E63329] text-white border-[#1a1a1a]' : 'bg-white text-[#1a1a1a] border-[#1a1a1a] hover:bg-[#fff2f0]'}`}>
                    <span className={`w-4 h-4 rounded-full border-2 border-current flex items-center justify-center ${active ? 'bg-white text-[#E63329]' : ''}`}>{active && <span className="w-2 h-2 rounded-full bg-[#E63329]" />}</span>
                    <span className="flex-1">{c}</span>
                  </button>
                );
              })}
              <button type="button" onClick={() => { setShowOtherIndustry(true); setForm((s) => ({ ...s, industry: '' })); }}
                className={`text-left text-[12px] font-bold py-2.5 px-3 rounded-lg border-2 transition-colors flex items-center gap-2 ${showOtherIndustry ? 'bg-[#E63329] text-white border-[#1a1a1a]' : 'bg-white text-[#1a1a1a] border-[#1a1a1a] hover:bg-[#fff2f0]'}`}>
                <span className={`w-4 h-4 rounded-full border-2 border-current flex items-center justify-center ${showOtherIndustry ? 'bg-white text-[#E63329]' : ''}`}>{showOtherIndustry && <span className="w-2 h-2 rounded-full bg-[#E63329]" />}</span>
                <span className="flex-1">Other…</span>
              </button>
            </div>
            {showOtherIndustry && (
              <input value={form.other_industry} onChange={(e) => update('other_industry', e.target.value)} placeholder="Sebutkan industry lain"
                className="mt-2 w-full h-11 px-3 rounded-lg border-2 border-[#1a1a1a] bg-white text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#E63329]" />
            )}
          </Field>

          <Field label="COMPANY WEBSITE" hint="Opsional">
            <Input value={form.company_website} onChange={(v) => update('company_website', v)} placeholder="https://…" leftIcon={<Globe className="w-4 h-4 text-black/50"/>} />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="PIC NAME" required>
              <Input value={form.pic_name} onChange={(v) => update('pic_name', v)} placeholder="Nama penanggung jawab" />
            </Field>
            <Field label="POSITION / TITLE" hint="Opsional">
              <Input value={form.pic_position} onChange={(v) => update('pic_position', v)} placeholder="Marketing Manager, dsb." />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="PIC EMAIL" required>
              <Input type="email" value={form.pic_email} onChange={(v) => update('pic_email', v)} placeholder="you@company.com" leftIcon={<Mail className="w-4 h-4 text-black/50"/>} />
            </Field>
            <Field label="PIC PHONE (WHATSAPP)" required>
              <Input value={form.pic_phone} onChange={(v) => update('pic_phone', v)} placeholder="+62 812 3456 7890" />
            </Field>
          </div>

          <div className="pt-1">
            {isFnB && (
              <div className="mb-3 rounded-lg border-2 border-amber-500 bg-amber-50 p-3 flex items-start gap-2 text-[13px] text-amber-900">
                <span className="text-lg leading-none">🍽️</span>
                <div>
                  <b>Pendaftaran F&amp;B belum dibuka.</b><br/>
                  Registrasi kategori <b>Food &amp; Beverage</b> akan dibuka pada <b>Oktober–November 2026</b>. Silakan follow <b>@talesverse</b> untuk update, atau pilih kategori lain untuk mendaftar sekarang.
                </div>
              </div>
            )}
            <button type="submit" disabled={submitting || isFnB}
              className="tv-btn w-full py-3.5 font-display text-[15px] tracking-wide uppercase disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: isFnB ? '#e5e5e5' : '#F5C518' }}>
              {submitting ? (<span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Mengirim…</span>) : isFnB ? 'F&B Registration — Coming Soon' : 'Submit Brand Application'}
            </button>
            <p className="text-[11px] text-black/60 text-center mt-2">Panitia akan menghubungi Anda dalam 2×24 jam kerja.</p>
          </div>
        </form>

        <div className="text-center text-white/85 text-[11px] mt-5">
          Brand inquiries • Email <b>partnership@luckytalesgroup.com</b> • WhatsApp <b>+62 857-7776-6625</b>
        </div>
      </div>
      <Marquee />
    </div>
  );
}

function SuccessScreen({ onNew, onHome }) {
  return (
    <div className="min-h-screen w-full bg-[#E63329] flex items-center justify-center px-5 py-10">
      <div className="bg-white text-[#1a1a1a] rounded-2xl border-[2.5px] border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-md w-full p-8 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-3" strokeWidth={2.5} />
        <div className="outline-title text-3xl mb-2">Terima Kasih!</div>
        <p className="text-[14px] font-medium mb-6">
          Pendaftaran <b>Brand Booth TalesVerse</b> berhasil dikirim. Tim <b>partnership</b> akan menghubungi kamu dalam 2×24 jam kerja untuk konfirmasi ketersediaan booth.
        </p>
        <div className="flex gap-3 justify-center">
          <button onClick={onNew} className="tv-btn px-4 py-2 text-sm font-semibold">Daftar lagi</button>
          <button onClick={onHome} className="tv-btn px-4 py-2 text-sm font-semibold" style={{ backgroundColor: '#F5C518' }}>Ke Beranda</button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-full bg-[#ffe3df] border-2 border-[#1a1a1a] flex items-center justify-center flex-shrink-0">{icon}</div>
      <span className="font-semibold">{label}</span>
    </div>
  );
}

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <div className="text-[11px] font-black uppercase tracking-widest text-black/80 mb-1.5">
        {label} {required && <span className="text-[#E63329]">*</span>}
        {hint && <span className="ml-2 text-[10px] font-medium text-black/50 normal-case tracking-normal">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text', leftIcon }) {
  return (
    <div className="relative">
      {leftIcon && <div className="absolute left-3 top-1/2 -translate-y-1/2">{leftIcon}</div>}
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={`w-full h-11 pr-3 rounded-lg border-2 border-[#1a1a1a] bg-white text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#E63329] ${leftIcon ? 'pl-9' : 'pl-3'}`} />
    </div>
  );
}

export default function CorporatePage() {
  return <RegGate kind="brand"><CorporatePageForm /></RegGate>;
}
